export interface ConsoleEntry {
  level: 'error' | 'warn';
  message: string;
  timestamp: string;
}

export interface NetworkEntry {
  method: string;
  url: string;
  baseURL?: string | null;
  resolvedUrl?: string | null;
  status: number | null;
  statusText?: string | null;
  durationMs: number | null;
  requestId?: string | null;
  responseRequestId?: string | null;
  requestContentType?: string | null;
  requestContentLength?: number | null;
  timeoutMs?: number | null;
  error: string | null;
  errorName?: string | null;
  errorCode?: string | null;
  hasResponse?: boolean;
  hasRequest?: boolean;
  isTimeout?: boolean;
  responseErrorCode?: string | null;
  responseErrorMessage?: string | null;
  timestamp: string;
}

export interface FormDebugData {
  formData: Record<string, unknown>;
  validationErrors: Record<string, string>;
  currentStep: number;
  stepCount: number;
}

export type FormDebugType = 'tutor' | 'parent' | 'music_tutor' | 'music_parent';

export interface PageDebugData {
  page: string;
  state: Record<string, unknown>;
}

export interface BreadcrumbEntry {
  category: 'navigation' | 'user' | 'upload' | 'api' | 'system';
  message: string;
  metadata?: Record<string, unknown>;
  timestamp: string;
}

declare global {
  interface Window {
    __debugInit?: boolean;
    __debugBridge?: {
      registerFormData: (type: FormDebugType, getData: () => FormDebugData) => void;
      unregisterFormData: (type: FormDebugType) => void;
      getFormDebugData: () => { type: FormDebugType; data: FormDebugData; isSnapshot: boolean } | null;
      registerPageData: (page: string, getData: () => PageDebugData) => void;
      unregisterPageData: (page: string) => void;
      getPageDebugData: () => PageDebugData | null;
      addBreadcrumb: (entry: Omit<BreadcrumbEntry, 'timestamp'>) => void;
      onFooterTap: (() => void) | null;
      triggerDebugReport: (() => void) | null;
    };
  }
}

const MAX_CONSOLE = 100;
const MAX_NETWORK = 50;
const MAX_REJECTIONS = 20;
const MAX_BREADCRUMBS = 60;
const SENSITIVE_KEYS = /password|token|secret|cvv|ssn/i;

export const debugStore: {
  consoleEntries: ConsoleEntry[];
  networkEntries: NetworkEntry[];
  rejections: string[];
  breadcrumbs: BreadcrumbEntry[];
} = {
  consoleEntries: [],
  networkEntries: [],
  rejections: [],
  breadcrumbs: [],
};

function safeStringify(args: unknown[]): string {
  try {
    return JSON.stringify(args).slice(0, 500);
  } catch {
    return args.map((a) => String(a).slice(0, 200)).join(', ');
  }
}

function sanitizeMetadata(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeMetadata(item));
  }

  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, nestedValue] of Object.entries(value)) {
      result[key] = SENSITIVE_KEYS.test(key)
        ? '[REDACTED]'
        : sanitizeMetadata(nestedValue);
    }
    return result;
  }

  return value;
}

function pushConsole(level: 'error' | 'warn', args: unknown[]) {
  const message = args.map((a) => (typeof a === 'string' ? a : safeStringify([a]))).join(' ');
  if (debugStore.consoleEntries.length >= MAX_CONSOLE) {
    debugStore.consoleEntries.shift();
  }
  debugStore.consoleEntries.push({ level, message, timestamp: new Date().toISOString() });
}

let lastFormSnapshot: { type: FormDebugType; data: FormDebugData; isSnapshot: boolean } | null = null;

export function initDebugCollectors() {
  if (window.__debugInit) return;
  window.__debugInit = true;

  // Initialize bridge
  const formDataMap = new Map<FormDebugType, () => FormDebugData>();
  const pageDataMap = new Map<string, () => PageDebugData>();
  window.__debugBridge = {
    registerFormData: (type, getData) => formDataMap.set(type, getData),
    unregisterFormData: (type) => {
      const getter = formDataMap.get(type);
      if (getter) {
        try { lastFormSnapshot = { type, data: getter(), isSnapshot: true }; } catch { /* ignore teardown errors */ }
      }
      formDataMap.delete(type);
    },
    getFormDebugData: () => {
      for (const [type, getData] of formDataMap) {
        return { type, data: getData(), isSnapshot: false };
      }
      return lastFormSnapshot;
    },
    registerPageData: (page, getData) => pageDataMap.set(page, getData),
    unregisterPageData: (page) => pageDataMap.delete(page),
    getPageDebugData: () => {
      for (const [, getData] of pageDataMap) {
        return getData();
      }
      return null;
    },
    addBreadcrumb: (entry) => pushBreadcrumb(entry),
    onFooterTap: null,
    triggerDebugReport: null,
  };

  // Patch console.error
  const originalError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    pushConsole('error', args);
    originalError(...args);
  };

  // Patch console.warn
  const originalWarn = console.warn.bind(console);
  console.warn = (...args: unknown[]) => {
    pushConsole('warn', args);
    originalWarn(...args);
  };

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason instanceof Error
      ? event.reason.message
      : String(event.reason).slice(0, 300);
    if (debugStore.rejections.length >= MAX_REJECTIONS) {
      debugStore.rejections.shift();
    }
    debugStore.rejections.push(`[${new Date().toISOString()}] ${reason}`);
  });

  // Global script errors
  window.addEventListener('error', (event) => {
    pushConsole('error', [`[GlobalError] ${event.message} at ${event.filename}:${event.lineno}`]);
  });
}

export function pushNetworkEntry(entry: NetworkEntry) {
  if (debugStore.networkEntries.length >= MAX_NETWORK) {
    debugStore.networkEntries.shift();
  }
  debugStore.networkEntries.push(entry);
}

export function captureError(source: string, error: unknown) {
  const message = error instanceof Error
    ? `[${source}] ${error.name}: ${error.message}`
    : `[${source}] ${String(error).slice(0, 300)}`;
  pushConsole('error', [message]);
}

export function pushBreadcrumb(entry: Omit<BreadcrumbEntry, 'timestamp'>) {
  if (debugStore.breadcrumbs.length >= MAX_BREADCRUMBS) {
    debugStore.breadcrumbs.shift();
  }
  debugStore.breadcrumbs.push({
    ...entry,
    metadata: entry.metadata ? sanitizeMetadata(entry.metadata) as Record<string, unknown> : undefined,
    timestamp: new Date().toISOString(),
  });
}
